import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ApiError } from '@/lib/api/client';
import { toast } from '@/hooks/useToast';
import {
  fetchTwoStepStatus, regenerateRecoveryCodes, disableTwoStep, type TwoStepStatus, type CodeProof,
} from './api';
import { SetupPanel } from './SetupPanel';
import { RecoveryCodesPanel } from './RecoveryCodesPanel';
import { CodeEntry } from './CodeEntry';

type View = 'status' | 'setup' | 'codes' | 'confirm-new-codes' | 'confirm-off';

const errorText = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : 'Something went wrong');

/** Profile menu → 🔐 Two-step login: set up, new recovery codes, turn off. */
export function TwoStepDialog({ open, onOpenChange, username }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  username: string;
}) {
  const [status, setStatus] = useState<TwoStepStatus | null>(null);
  const [view, setView] = useState<View>('status');
  const [codes, setCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const load = () => fetchTwoStepStatus().then(setStatus).catch((e) => setError(errorText(e)));

  useEffect(() => {
    if (!open) return;
    setView('status'); setError(null); setStatus(null);
    load();
  }, [open]);

  const withCode = (fn: (proof: CodeProof) => Promise<void>) => async (proof: CodeProof) => {
    setPending(true); setError(null);
    try { await fn(proof); } catch (e) { setError(errorText(e)); } finally { setPending(false); }
  };

  const title = view === 'setup' ? 'Set up two-step login'
    : view === 'codes' ? 'Save your recovery codes'
    : view === 'confirm-new-codes' ? 'New recovery codes'
    : view === 'confirm-off' ? 'Turn off two-step login'
    : 'Two-step login';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-110">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {view === 'status' && (
            <DialogDescription>
              Signing in asks for your password and a code from an app on your phone, so a stolen password alone isn’t enough.
            </DialogDescription>
          )}
        </DialogHeader>

        {view === 'status' && (
          !status ? (
            error ? <div className="text-xs text-danger-ink">{error}</div> : <div className="py-4 text-sm text-ink-muted">Loading…</div>
          ) : (
            <div className="flex flex-col gap-3 text-[13px]">
              <div className="flex items-center gap-2">
                <span className="text-ink">Status</span>
                {status.mfa_enabled ? <Badge variant="success">On</Badge> : <Badge variant="neutral">Off</Badge>}
                {status.required && <Badge variant="info">Required for your role</Badge>}
              </div>
              {status.mfa_enabled ? (
                <>
                  <div className="text-ink-muted">
                    Recovery codes left: <span className="font-semibold text-ink">{status.recovery_codes_left}</span>
                    {status.recovery_codes_left <= 3 && ' — make a new set soon.'}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { setError(null); setView('confirm-new-codes'); }}>New recovery codes</Button>
                    {!status.required && (
                      <Button variant="ghost" size="sm" onClick={() => { setError(null); setView('confirm-off'); }}>Turn off</Button>
                    )}
                  </div>
                </>
              ) : (
                <Button onClick={() => setView('setup')}>Set up two-step login</Button>
              )}
            </div>
          )
        )}

        {view === 'setup' && (
          <SetupPanel onEnabled={(r) => { setCodes(r.recovery_codes); setView('codes'); }} />
        )}

        {view === 'codes' && (
          <RecoveryCodesPanel codes={codes} account={username} doneLabel="Done"
            onDone={() => { setView('status'); load(); }} />
        )}

        {view === 'confirm-new-codes' && (
          <>
            <p className="text-[13px] text-ink">Your current recovery codes will stop working. Enter a code from your app to continue.</p>
            <CodeEntry submitLabel="Make new codes" pending={pending} error={error}
              onSubmit={withCode(async (proof) => {
                const r = await regenerateRecoveryCodes(proof);
                setCodes(r.recovery_codes); setView('codes');
              })} />
          </>
        )}

        {view === 'confirm-off' && (
          <>
            <p className="text-[13px] text-ink">Signing in will need only your password. Enter a code from your app to confirm.</p>
            <CodeEntry submitLabel="Turn off" pending={pending} error={error}
              onSubmit={withCode(async (proof) => {
                await disableTwoStep(proof);
                toast({ title: 'Two-step login is off', variant: 'success' });
                setView('status'); await load();
              })} />
          </>
        )}

        {view !== 'status' && view !== 'codes' && (
          <Button variant="link" className="self-start text-xs" onClick={() => { setError(null); setView('status'); }}>← Back</Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
